import { MdsButton, MdsButtonVariant } from "@/design-system/components/button";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { MDSButton } from "@/mds/components/buttons";
import { MDSSection, MDSTextField } from "@/mds";
import { useAppStore } from "@/store";
import styled from "@emotion/styled";
import { PendingApiKey } from "@/pages/integrations/api/integrations-api-page.types";
import { observer } from "mobx-react-lite";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { useCallback } from "react";

type TProps = {
  nickname: string;
  pendingApiKey: PendingApiKey | null;
  copyApiKey: () => void;
  handleCloseForm: () => void;
  setNickname: (nickname: string) => void;
};

export const IntegrationsApiPageContentForm = observer<TProps>(
  ({ nickname, pendingApiKey, copyApiKey, handleCloseForm, setNickname }: TProps) => {
    const { store } = useAppStore();

    const submitForm = useCallback(() => {
      handleCloseForm();
      setNickname("");

      if (!pendingApiKey) {
        return;
      }

      store.apiKeys.updateApiKey({
        id: pendingApiKey.id,
        title: nickname || null,
      });

      trackEvent(TrackedEvent.ApiKeyName, {
        api_key_id: pendingApiKey.id,
        api_key_name: nickname,
      });
    }, [handleCloseForm, nickname, pendingApiKey, setNickname, store.apiKeys]);

    return (
      <StyledForm>
        <StyledSection title={<StyledTitle>Add new API key</StyledTitle>}>
          <Disclaimer>
            Make sure to save your secret key;{" "}
            <EmphasizedText>this key won&apos;t be shown again.</EmphasizedText>
          </Disclaimer>
          <SecretKeyText>
            {`sk-mem-${pendingApiKey?.secretKeyToken}`}
            <MDSButton color="transparent" onPress={copyApiKey} size="sm">
              <MdsIcon kind={MdsIconKind.Copy}></MdsIcon>
            </MDSButton>
          </SecretKeyText>
          <MDSTextField
            type="text"
            value={nickname}
            onChange={setNickname}
            placeholder="Enter nickname"
            aria-label="Nickname"
            autoFocus
          />
          <FooterStyles>
            <MdsButton variant={MdsButtonVariant.FilledDark} label="Done" onClick={submitForm} />
          </FooterStyles>
        </StyledSection>
      </StyledForm>
    );
  }
);

const StyledForm = styled.form(() => ({
  width: "100%",
}));

const StyledSection = styled(MDSSection)(() => ({
  width: "100%",
}));

const StyledTitle = styled.div(({ theme }) => ({
  fontSize: theme.fontSizes.xsmall,
  fontWeight: theme.fontWeights.semiBold,
  lineHeight: theme.lineHeights.xsmall,
  color: theme.colors.grey.x500,
}));

const Disclaimer = styled.div(({ theme }) => ({
  color: theme.colors.grey.x600,
  fontSize: theme.fontSizes.small,
  marginBottom: theme.spacing.md,
}));

const FooterStyles = styled.div({
  alignSelf: "flex-end",
  display: "flex",
  gap: "8px",
  marginTop: "8px",
});

const SecretKeyText = styled.div(({ theme }) => ({
  fontFamily: "monaco, monospace",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing.sm,
  padding: `1px ${theme.spacing.sm}`,
  backgroundColor: theme.colors.grey.x25,
  border: `1px solid ${theme.colors.grey.x100}`,
  borderRadius: theme.borderRadius.medium,
  width: "100%",

  "& svg": {
    height: 16,
    width: 16,
    color: theme.colors.grey.x400,
  },
}));

const EmphasizedText = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeights.medium,
}));
